import React from "react";
import { Grid, Typography } from "@mui/material";
import { FormattedMessage } from "react-intl";
import { Link, useNavigate } from "react-router-dom";

import GBSCLogo from "@/assets/GBSCLogo.svg";

import style from "./index.module.css";

import CopyWriteFooter from "./CopyWriteFooter";
import GetImages from "@/components/GetImages";
import Button from "@/components/Button";
import DownloadAppBanner from "@/components/DownloadAppBanner";

import { IButtonVariant } from "@/models/button";
import {
  IAddPatient,
  IKeyPairsValues,
  IPatientExistOrNotModule,
} from "@/models/doctor";
import { CONVERSATION_TYPE } from "@/models/chat";
import {
  IRedirectAfterLoginAction,
  IRedirectAfterLoginType,
  ISessionKeys,
} from "@/models";
import { IRoleType } from "@/models/role";

import { patientFooterData } from "@/utils/common";
import { firstRouteMap, secondRouteMap, thirdRouteMap } from "@/utils";
import { appLinks, supportNumber } from "@/utils/Config";
import { storage } from "@/utils/Storage";

import { setSelectedChat } from "@/store/slices/chat";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { setModuleName } from "@/store/slices/doctor/checkMobilePatientExistOrNot";
import { setAddPatient } from "@/store/slices/doctor/topNavigation";
import { config, setRedirectAfterLogin } from "@/store/slices/config";

import { useFirebaseMessagingContext } from "@/context/Chat";

const DesktopBottomNavigation: React.FC = () => {
  const navigate = useNavigate();
  const keys = [ISessionKeys.USER_ID, ISessionKeys.ROLE_TYPE];
  const [userId, roleType] = keys.map((key) => storage.getSessionToken(key));

  const dispatch = useAppDispatch();
  const { handleChannel } = useFirebaseMessagingContext();
  const { redirectAfterLogin } = useAppSelector(config);

  const { first, second, third, socialIconsList, popularSearches } =
    patientFooterData;

  const handleNavigation = (item: string, routeMap: IKeyPairsValues) => {
    if (item === "chat_with_us" || item === "free_MRI_CT_review") {
      if (roleType === IRoleType.GUEST) {
        dispatch(setModuleName(IPatientExistOrNotModule.LOGIN));
        dispatch(
          setAddPatient({
            field: IAddPatient.MOBILE_CHECK,
            value: true,
          })
        );
        dispatch(
          setRedirectAfterLogin({
            ...redirectAfterLogin,
            path: "/find-doctor",
            isRedirect: true,
            navItem: 1,
            type: IRedirectAfterLoginType.FIND_DOCTOR,
            action: IRedirectAfterLoginAction.APPOINTMENT_CREATION,
          })
        );
        return;
      } else {
        dispatch(
          setSelectedChat({
            id: userId,
            uniqueId: `sup_${userId}`,
            type: CONVERSATION_TYPE.SUPPORT,
            users: {
              doctor_id: "",
              patient_id: userId,
              staff_members: [],
              family_members: [],
              admin_id: "",
              admin_members: [],
            },
          })
        );
        handleChannel(`sup_${userId}`);
      }
    }
    if (routeMap[item as keyof typeof routeMap]) {
      navigate(routeMap[item as keyof typeof routeMap]);
    }
  };

  return (
    <Grid item>
      <DownloadAppBanner />
      <Grid item className={style.bottomNavigationContainer}>
        <Grid item>
          {first?.map((navItem, index) => (
            <Grid item key={`${navItem.title}-${index}`}>
              <Typography component={"p"} className={style.linkTitle}>
                <FormattedMessage id={navItem.title} defaultMessage="Title" />
              </Typography>
              {navItem.items.map((item, index) => (
                <Typography
                  component={"p"}
                  key={index}
                  className={style.linkText}
                  onClick={() => handleNavigation(item, firstRouteMap)}
                  style={
                    firstRouteMap[item as keyof typeof firstRouteMap]
                      ? { cursor: "pointer" }
                      : {}
                  }
                >
                  <FormattedMessage id={item} defaultMessage="Items" />
                </Typography>
              ))}
            </Grid>
          ))}
        </Grid>
        <Grid item>
          {second?.map((navItem, index) => (
            <Grid item key={`${navItem.title}-${index}`}>
              <Typography component={"p"} className={style.linkTitle}>
                <FormattedMessage id={navItem.title} defaultMessage="Title" />
              </Typography>
              {navItem.items.map((item, index) => (
                <Typography
                  component={"p"}
                  key={index}
                  className={style.linkText}
                  onClick={() => handleNavigation(item, secondRouteMap)}
                  style={
                    secondRouteMap[item as keyof typeof secondRouteMap]
                      ? { cursor: "pointer" }
                      : {}
                  }
                >
                  <FormattedMessage id={item} defaultMessage="Items" />
                </Typography>
              ))}
            </Grid>
          ))}
        </Grid>
        <Grid item>
          {third?.map((navItem, index) => (
            <Grid item key={`${navItem.title}-${index}`}>
              <Typography component={"p"} className={style.linkTitle}>
                <FormattedMessage id={navItem.title} defaultMessage="Title" />
              </Typography>
              {navItem.items.map((item, index) => (
                <Typography
                  component={"p"}
                  key={index}
                  className={style.linkText}
                  onClick={() => handleNavigation(item, thirdRouteMap)}
                  style={
                    thirdRouteMap[item as keyof typeof thirdRouteMap]
                      ? { cursor: "pointer" }
                      : {}
                  }
                >
                  <FormattedMessage id={item} defaultMessage="Items" />
                </Typography>
              ))}
            </Grid>
          ))}
        </Grid>
        <Grid item>
          <Link to="/home">
            <img src={GBSCLogo} alt="logo" width="116" height="67" />
          </Link>
          <Grid item className={style.downloadAppBtnContainer}>
            <Button
              variant={IButtonVariant.WHITE}
              btnTrigger={() => window.open(appLinks?.android, "_blank")}
            >
              <GetImages name="MobileIcon" width="28" height="24" />
              <FormattedMessage
                id="download_app"
                defaultMessage="Download the app"
              />
            </Button>
          </Grid>
          <Grid item className={style.customerSupportContainer}>
            <Typography component={"p"}>
              <FormattedMessage
                id="customer_support"
                defaultMessage="Customer Support"
              />
            </Typography>
            <Grid item className={style.telePhoneNumber}>
              <GetImages name="TelePhoneIcon" width="24" height="20" />
              <Typography component={"p"}>+91-{supportNumber}</Typography>
            </Grid>
            <Grid item className={style.timingContactEmailContainer}>
              <Typography component={"p"}>24×7 Support, Every Day</Typography>
              <Grid item className={style.emailContainer}>
                <GetImages name="MessageOrangeIcon" width="32" height="20" />
                <Typography component={"p"}>
                  support@globalneuroandspinecare.com
                </Typography>
              </Grid>
            </Grid>
          </Grid>
          <Grid item className={style.socialAccountsContainer}>
            <Typography component={"p"}>
              <FormattedMessage
                id="connect_with_us"
                defaultMessage="Connect with us on"
              />
            </Typography>
            <Grid item className={style.socialLinks}>
              {socialIconsList?.map((social, index) => (
                <Link
                  to={social?.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  key={`${social?.iconName}-${index}`}
                >
                  <Grid item key={index} className={style.iconOuterContainer}>
                    <GetImages name={social?.iconName} width="20" height="20" />
                  </Grid>
                </Link>
              ))}
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <Grid item className={style.popularSeaches}>
        <Typography component={"p"} className={style.linkTitle}>
          <FormattedMessage id={"popular_searches"} defaultMessage="Title" />
        </Typography>
        {popularSearches.map((searches, index) => (
          <Typography
            key={`${searches}-${index}`}
            component={"span"}
            className={style.linkText}
          >
            <Link
              to={"/find-doctor"}
              rel="noopener noreferrer"
              className={style.link}
            >
              {searches}
            </Link>{" "}
            {popularSearches.length - 1 !== index && " | "}
          </Typography>
        ))}
      </Grid>
      <CopyWriteFooter />
    </Grid>
  );
};

export default DesktopBottomNavigation;
